import React, { useState, useEffect, FC } from 'react'
import { useLatestUsedCredentialsQuery } from '../../credentials/api/use-latest-used-credentials'
import { useJiraIntegrationPage } from './jira-integration-page.provider'
import { Header } from './header'
import { Footer } from './footer/footer'
import { CredentialsFields } from './credentials-fields'
import { useForm, FormProvider, SubmitHandler } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import { AssessFields } from './assess-fields'
import { ServerlessFields } from './serverless-fields'
import { ProductSelectionField } from './product-selection-field'
import { useAddJiraBugtrackerQuery } from '../api/use-add-jira-bugtracker-query'
import {
  BugtrackerConfig,
  NgBugtrackerMaintenanceWindowResource
} from '../api/shared'
import {
  ConnectionStatus,
  updateAnyState
} from './state/jira-integration-page.state'
import { useTestBugtrackerQuery } from '../../bugtrackers/api/use-test-bugtracker-query'
import { Row } from '../../../../legacy-design-system/form'
import { BugtrackerRequest } from '../../bugtrackers/api/shared'
import { useGetJiraBugtrackerQuery } from '../api/use-get-jira-bugtracker-query'
import { toast } from '@contrast-security-inc/design-system-alerts'
import { formDefaultValues, JiraFormFieldTypes } from './jira-form-field-types'
import { useUpdateJiraBugtrackerQuery } from '../api/use-update-jira-bugtracker-query'
import { mockJiraBugtracker } from '../api/mocks/jira-bugtracker'
import { useQueryClient } from 'react-query'
import { Query } from '../../../../hooks/queries'
import { getIntegrationsRoute } from '../../../route'
import { mapApiBugtrackerToUI } from '../api/map-jira-form'
import { LatestUsedCredentials } from '../../credentials/api/shared'
import { resetConnectionState } from './state/jira-integration-page-actions'
import { CredentialsUpdateConfirmationDialog } from './credentials-update-confirmation-dialog'
import { JiraIntegrationForm, formSchema } from './jira-form-schema'
import { useOrganization } from '../../../organization-licenses/api/use-organization'

export const transformFormValuesToCredentialsRequest = ({
  formValues,
  isManagingCredentials,
  hasPreviousCredentials
}: {
  formValues: JiraIntegrationForm
  isManagingCredentials: boolean
  hasPreviousCredentials: boolean
}): BugtrackerRequest => {
  const request: BugtrackerRequest = {
    name:
      !formValues.name && formValues.credentials_id
        ? 'Default Test Connection'
        : formValues.name
  }
  if (!isManagingCredentials && formValues.credentials_id) {
    request.credentials_id = formValues.credentials_id
  }

  if (
    isManagingCredentials ||
    (formValues.credentials &&
      formValues.credentials.username &&
      formValues.credentials.host &&
      formValues.credentials.password)
  ) {
    if (formValues?.credentials) {
      request.credentials = {
        username: formValues.credentials.username,
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        password: formValues.credentials.password!,
        host: formValues.credentials.host,
        //  Legacy behavior, if there are no "credentials" previously saved for ANY connection, then default the initial
        //  connection to have " credentials" suffix. TBD if this should be eventually replaced
        name: hasPreviousCredentials
          ? `${formValues.credentials.name} credentials`
          : formValues.credentials.name
      }
    }
  }

  return request
}

export const JiraIntegrationPage = () => {
  const [isBugtrackerLoaded, setIsBugtrackerLoaded] = useState(false)

  const {
    state: { organizationUuid, bugtrackerId }
  } = useJiraIntegrationPage()
  // eslint-disable-next-line @typescript-eslint/naming-convention
  const { bugtracker, integrations, maintenanceWindow } =
    useGetJiraBugtrackerQuery({
      organizationUuid,
      bugtrackerId: bugtrackerId?.toString() || ''
    })

  useEffect(() => {
    if (bugtracker) {
      setIsBugtrackerLoaded(true)
    }
  }, [bugtracker])

  if (!bugtrackerId) {
    return (
      <JiraIntegration
        bugtracker={undefined}
        integrations={undefined}
        maintenanceWindow={undefined}
      />
    )
  }

  return isBugtrackerLoaded ? (
    <JiraIntegration
      bugtracker={bugtracker}
      integrations={integrations}
      maintenanceWindow={maintenanceWindow}
    />
  ) : null
}

const JiraIntegration = ({ bugtracker, integrations, maintenanceWindow }) => {
  const { state: pageState, dispatch } = useJiraIntegrationPage()

  const {
    organizationUuid,
    bugtrackerId,
    previousSavedCredentialsName,
    isManagingCredentials,
    connectionStatus,
    hasCredentialsUpdate,
    isEdit
  } = pageState
  const [isCredentialsConfirmationOpen, setCredentialsConfirmationOpen] =
    useState(false)
  const [bugtrackerRequest, setBugtrackerRequest] =
    useState<null | BugtrackerRequest>(null)
  const { data } = useOrganization({ organizationUuid })
  const isCloudNativeEnabled = !!data?.cloudnativeEnabled

  const methods = useForm<JiraIntegrationForm>({
    resolver: yupResolver(formSchema),
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues: bugtracker
      ? mapApiBugtrackerToUI(bugtracker, integrations, maintenanceWindow)
      : formDefaultValues
  })

  const queryClient = useQueryClient()

  const hasPreviousCredentials = !!previousSavedCredentialsName

  //  Test the connection once the bugtracker loads, when editing an existing connection
  useEffect(() => {
    if (isEdit && bugtracker) {
      dispatch(
        updateAnyState({
          previousSavedCredentialsName: bugtracker.credentials.name
        })
      )
      testConnection()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEdit, bugtracker])

  useLatestUsedCredentialsQuery({
    organizationUuid,
    bugtracker: 'JIRA',
    updateFormValue: methods.setValue,
    //  Disable the query so the "latest" credentials don't override a previously assigned set of credentials to the connection
    disabled: isEdit,
    successCallback: (data: LatestUsedCredentials) => {
      dispatch(
        updateAnyState({
          previousSavedCredentialsName: data.name,
          previousDraftCredentials: {
            host: data.host,
            id: data.id,
            username: data.username,
            name: data.name
          }
        })
      )
      testConnection()
    }
  })

  const testConnection = () => {
    resetConnectionState({
      dispatch,
      pageState,
      connectionStatus: 'testing'
    })

    dispatch(
      updateAnyState({
        errorCode: null,
        errorMessages: []
      })
    )

    //  TODO: Is this still needed?
    methods.setValue(
      JiraFormFieldTypes.CREDENTIALS_HOST,
      methods
        .getValues(JiraFormFieldTypes.CREDENTIALS_HOST)
        .replace(/(\/)$/, '')
    )

    const newBugtrackerRequest = transformFormValuesToCredentialsRequest({
      formValues: methods.getValues(),
      isManagingCredentials,
      hasPreviousCredentials
    })
    setBugtrackerRequest(newBugtrackerRequest)

    /**
     * Handle an edge case where the user modifies the credentials, and we need to retest the previously tested (and cached) credentials to
     * provide an error/success message, and update the connection button status correctly
     */
    if (
      newBugtrackerRequest.credentials?.host ===
        bugtrackerRequest?.credentials?.host &&
      newBugtrackerRequest.credentials?.name ===
        bugtrackerRequest?.credentials?.name &&
      newBugtrackerRequest.credentials?.password ===
        bugtrackerRequest?.credentials?.password
    ) {
      queryClient
        .invalidateQueries([Query.INTEGRATIONS_TEST_BUGTRACKER])
        // eslint-disable-next-line no-console
        .catch(console.error)
    }
  }

  const { mutate: mutateAddBugtracker } = useAddJiraBugtrackerQuery({
    onSettled: (_data, error) => {
      if (error) {
        toast(error.message, { variant: 'error' })
      } else {
        toast('JIRA configuration connected successfully')
        location.href = getIntegrationsRoute()
      }
    }
  })

  const { mutate: mutateUpdateBugtracker } = useUpdateJiraBugtrackerQuery({
    onSettled: async (_data, error) => {
      if (error) {
        toast(error.message, { variant: 'error' })
      } else {
        await queryClient.invalidateQueries([
          Query.INTEGRATIONS_PROJECT_FIELDS_JIRA
        ])
        await queryClient.invalidateQueries([
          Query.INTEGRATIONS_PROJECT_USERS_JIRA
        ])
        await queryClient.invalidateQueries([
          Query.INTEGRATIONS_GET_JIRA_BUGTRACKER,
          organizationUuid,
          bugtrackerId?.toString() || ''
        ])
        toast('JIRA configuration updated successfully')
        location.href = getIntegrationsRoute()
      }
    }
  })

  useTestBugtrackerQuery({
    organizationUuid,
    bugtrackerId,
    bugtrackerRequest,
    bugtrackerType: 'jira',
    disableQuery: connectionStatus === 'untested',
    successCallback: () => {
      dispatch(
        updateAnyState({
          connectionStatus: 'valid'
        })
      )
    },
    errorCallback: () => {
      dispatch(
        updateAnyState({
          connectionStatus: 'invalid'
        })
      )
    }
  })

  const deleteIntegration = () => {
    // This functionality will be done in the upcoming PRs
    // after the form and the requests are implemented
  }

  const submit = (newCredentialsSetName?: string) => {
    setCredentialsConfirmationOpen(false)
    //  Do not send the mock credentials ID with the remaining "mock data" which is temporarily in place until all fields are supported
    const { credentials_id: _omittedMockId, ...mockUnimplementedData } =
      mockJiraBugtracker

    /*
     *  Manage Credentials Use Cases
     *  1. No existing credentials / connection - Currently Implemented
     *     - Should submit { credentials : { name, password, host, username }
     *  2. Add/Update a connection without affecting credentials - Currently Implemented
     *     - Should submit { credentials_id }
     *  3. Add/Update a connection with updated credentials, and overwrite existing credentials
     *     - Should submit { credentials_id, credentials : { name, password, host, username, credentials_update: true }}
     *  4. Add/Update a connection with updated credentials, and create a new credential set
     *     - Should submit { credentials_id, credentials : { name, password, host, username, credentials_update: false }}
     */
    // eslint-disable-next-line @typescript-eslint/naming-convention
    const { credentials, credentials_id, ...payloadFormValues } =
      methods.getValues()
    const submitCredentialsData: Partial<JiraIntegrationForm> = {
      credentials_id
    }

    if ((!isEdit || hasCredentialsUpdate) && credentials) {
      submitCredentialsData.credentials = {
        ...credentials,
        name: newCredentialsSetName ?? credentials.name,
        credentials_update: !newCredentialsSetName
      }
    }

    if (isEdit) {
      mutateUpdateBugtracker({
        organizationUuid,
        bugtracker: {
          ...mockUnimplementedData,
          ...payloadFormValues,
          ...submitCredentialsData
        },
        bugtrackerId
      })
    } else {
      mutateAddBugtracker({
        organizationUuid,
        bugtracker: {
          ...mockUnimplementedData,
          ...payloadFormValues,
          ...submitCredentialsData
        }
      })
    }
  }

  const handleFormSubmit: SubmitHandler<JiraIntegrationForm> = (values) => {
    if (
      hasCredentialsUpdate &&
      hasPreviousCredentials &&
      values.credentials?.password
    ) {
      setCredentialsConfirmationOpen(true)
    } else {
      submit()
    }
  }

  const isServerless = methods.watch('serverless')

  // eslint-disable-next-line @typescript-eslint/naming-convention
  const { credentials, credentials_id: credentialId } = methods.getValues()

  return (
    <FormProvider {...methods}>
      <form onSubmit={methods.handleSubmit(handleFormSubmit)}>
        <div className="jira-integration" data-testid={'jira-integration-page'}>
          <section className="panel drilldown panel-default table-dynamic">
            <div className="panel-body">
              <Header />
              <div className="divider" />
              <CredentialsFields />
              {!isManagingCredentials && (
                <ConfigurationsFormContent
                  hasPreviousCredentials={hasPreviousCredentials}
                  connectionStatus={connectionStatus}
                  isCloudNativeEnabled={isCloudNativeEnabled}
                  isServerless={isServerless}
                  bugtracker={bugtracker}
                  maintenanceWindow={maintenanceWindow}
                />
              )}
            </div>
          </section>
          <Footer
            credentialName={credentials?.name || ''}
            credentialId={credentialId || ''}
            deleteIntegration={deleteIntegration}
            testConnection={testConnection}
          />
        </div>
        <CredentialsUpdateConfirmationDialog
          isOpen={isCredentialsConfirmationOpen}
          cancelSubmit={() => setCredentialsConfirmationOpen(false)}
          submit={submit}
        />
      </form>
    </FormProvider>
  )
}

interface ConfigurationsFormContentProps {
  hasPreviousCredentials: boolean
  isCloudNativeEnabled: boolean
  isServerless: boolean
  bugtracker: BugtrackerConfig
  connectionStatus: ConnectionStatus
  maintenanceWindow: NgBugtrackerMaintenanceWindowResource
}

const ConfigurationsFormContent: FC<ConfigurationsFormContentProps> = ({
  hasPreviousCredentials,
  connectionStatus,
  isCloudNativeEnabled,
  isServerless,
  bugtracker,
  maintenanceWindow
}) => {
  const InnerFields: FC<{
    bugtracker?: BugtrackerConfig
    maintenanceWindow?: NgBugtrackerMaintenanceWindowResource
  }> = isServerless ? ServerlessFields : AssessFields
  return (
    <>
      {(hasPreviousCredentials || connectionStatus === 'valid') && (
        <>
          {isCloudNativeEnabled && (
            <Row>
              <ProductSelectionField />
            </Row>
          )}
          <InnerFields
            bugtracker={bugtracker}
            maintenanceWindow={maintenanceWindow}
          />
        </>
      )}
    </>
  )
}
